import {
	type ChatMessageDto,
	HITL_TOOL_NAMES,
	isHitlRejectionTurn,
	isToolResultContent,
	isToolUseContent,
} from "@/lib/chat";
import { toHomeDirRelative } from "@/lib/environment-files";
import { findPendingToolApproval, type ToolApprovalMarker } from "@/lib/tool-approval";
import { parseSuggestedSegments, type SuggestedSegment } from "./segmentation/suggestedSegments";
import { normalizeQuestions, type QuestionDef } from "./user-choice/useUserChoice";

/** セグメンテーション HITL の状態。 */
export interface SegmentationHITL {
	kind: "segmentation";
	toolUseId: string;
	/** セグメント対象動画のワークスペース相対パス (request_segmentation の video_path)。 */
	videoPath: string;
	/** 編集中の境界・ラベル。チャット切替をまたいで保持するためストアに持つ。 */
	boundaries?: readonly number[];
	labels?: readonly string[];
	suggestedSegments?: readonly SuggestedSegment[];
}

/** ユーザー選択 HITL の状態 (バッチ質問)。 */
export interface UserChoiceHITL {
	kind: "user-choice";
	toolUseId: string;
	questions: QuestionDef[];
}

/** サーバー実行ツールに対する承認待ち。元 assistant tool_use を表示・決定の正とする。 */
export interface ToolApprovalHITL {
	kind: "tool-approval";
	toolUseId: string;
	category: string;
	toolUse: {
		name: string;
		input: Record<string, unknown>;
	};
	marker: ToolApprovalMarker;
}

/**
 * クライアント実行 HITL の状態。`kind` による判別共用体で、各モード固有の
 * フィールドは該当モードでのみ存在する。バックエンドは未応答の HITL を同時に 1 件以下へ
 * 保証するため、単一スロットで表現する。
 */
export type HITLState = SegmentationHITL | UserChoiceHITL | ToolApprovalHITL;

/**
 * メッセージ列から「未応答の HITL tool_use」を探し、対応する HITL 状態を返す。
 * 対応する tool_result がまだ無い HITL ツール呼び出しが対象。該当が無ければ null。
 * バックエンドは未応答の HITL を同時に 1 件以下へ保つため、最初に見つけたものを返す。
 */
export function findPendingHITL(messages: ChatMessageDto[]): HITLState | null {
	const pendingApproval = findPendingToolApproval(messages);
	if (pendingApproval !== null) {
		return {
			kind: "tool-approval",
			toolUseId: pendingApproval.toolUse.id,
			category: pendingApproval.marker.category,
			toolUse: { name: pendingApproval.toolUse.name, input: pendingApproval.toolUse.input },
			marker: pendingApproval.marker,
		};
	}

	const answered = new Set<string>();
	for (const m of messages) {
		for (const b of m.content) {
			if (isToolResultContent(b)) answered.add(b.tool_use_id);
		}
	}

	for (const m of messages) {
		// 混在拒否ターンの HITL は応答待ちではないためパネルを出さない。判定がずれると、拒否済み HITL への
		// ユーザー応答が別 User メッセージへ着地し、同一ターンの tool_result が 2 通に分割される。
		if (isHitlRejectionTurn(m.content)) continue;

		for (const b of m.content) {
			if (!isToolUseContent(b) || !HITL_TOOL_NAMES.has(b.name) || answered.has(b.id)) {
				continue;
			}
			if (b.name === "request_user_choice") {
				return {
					kind: "user-choice",
					toolUseId: b.id,
					questions: normalizeQuestions(b.input.questions),
				};
			}
			if (b.name === "request_segmentation") {
				// エージェントは ~/ や /root/ 付きのパスを渡しうるため、索引キーと同じワークスペース相対形へ
				// 畳んでから扱う (動画ファイルの照合と segment 出力の video_id を索引と一致させる)。
				const rawVideoPath = (b.input.video_path as string) ?? "";
				return {
					kind: "segmentation",
					toolUseId: b.id,
					videoPath: toHomeDirRelative(rawVideoPath) ?? rawVideoPath,
					suggestedSegments: parseSuggestedSegments(b.input.suggested_segments),
				};
			}
		}
	}
	return null;
}
